// Attribution / UTM tracking for Bizenntrum GTM pilot.
// - Parses UTMs from window.location.search AND from window.location.hash
//   (supports "/?utm=…#anchor" AND "/#anchor?utm=…")
// - Session persistence (current visit) + first-touch persistence (~30 days) in localStorage
// - Captures `first_page_visited` once per browser (never overwritten)
// - Anchor aliases: #india-entry → #singapore-india, #strategy-call → #contact
// - Default utm_medium="outreach" if source/campaign present but medium missing

const SESSION_KEY = 'bizenntrum_attribution';        // current-visit attribution (sessionStorage)
const FIRST_TOUCH_KEY = 'bizenntrum_first_touch';    // first-touch attribution (localStorage, 30d)
const FIRST_PAGE_KEY = 'bizenntrum_first_page';      // first page visited (localStorage, 30d)
const FIRST_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const UTM_KEYS = ['utm_source', 'utm_campaign', 'utm_medium', 'utm_content', 'utm_term'];

const ANCHOR_ALIASES = {
  'india-entry': 'singapore-india',
  'strategy-call': 'contact',
};

function readParams(searchString) {
  if (!searchString) return {};
  const s = searchString.startsWith('?') ? searchString.slice(1) : searchString;
  const out = {};
  new URLSearchParams(s).forEach((v, k) => { out[k] = v; });
  return out;
}

function parseHash(rawHash) {
  if (!rawHash || rawHash === '#') return { anchor: null, params: {} };
  const h = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
  const qIndex = h.indexOf('?');
  if (qIndex === -1) return { anchor: h || null, params: {} };
  const anchor = h.slice(0, qIndex) || null;
  const params = readParams(h.slice(qIndex + 1));
  return { anchor, params };
}

function safeGetLocal(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed._expires_at && Date.now() > parsed._expires_at) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (_) { return null; }
}

function safeSetLocal(key, value) {
  try {
    const payload = { ...value, _expires_at: Date.now() + FIRST_TOUCH_TTL_MS };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (_) { /* ignore */ }
}

function safeSetSession(key, value) {
  try { window.sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* ignore */ }
}

function safeGetSession(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

/**
 * Initialise attribution tracking on app mount.
 * - Captures utm params from URL (search + hash) into sessionStorage
 * - Preserves first-touch attribution in localStorage for 30 days
 * - Records first_page_visited (never overwritten within TTL)
 * - Cleans #anchor?utm=... hashes and scrolls to the intended anchor
 */
export function initAttribution() {
  if (typeof window === 'undefined') return;

  const searchParams = readParams(window.location.search);
  const { anchor, params: hashParams } = parseHash(window.location.hash);
  const merged = { ...hashParams, ...searchParams }; // search wins

  // Extract UTM values from URL (may be empty)
  const utmPayload = {};
  UTM_KEYS.forEach((k) => {
    if (merged[k]) utmPayload[k] = merged[k];
  });

  const currentUrl = window.location.pathname + window.location.search + window.location.hash;
  const now = new Date().toISOString();

  // 1) Record first_page_visited exactly once per TTL window
  if (!safeGetLocal(FIRST_PAGE_KEY)) {
    safeSetLocal(FIRST_PAGE_KEY, { first_page_visited: currentUrl, captured_at: now });
  }

  if (Object.keys(utmPayload).length > 0) {
    // Default utm_medium = "outreach" if source/campaign given but medium missing
    if ((utmPayload.utm_source || utmPayload.utm_campaign) && !utmPayload.utm_medium) {
      utmPayload.utm_medium = 'outreach';
    }
    utmPayload.landing_page = currentUrl;
    utmPayload.captured_at = now;

    // Session (current-visit) attribution — always overwritten by a new tagged URL
    safeSetSession(SESSION_KEY, utmPayload);

    // First-touch attribution — set only if not already stored (unless new tagged campaign)
    const existingFirstTouch = safeGetLocal(FIRST_TOUCH_KEY);
    if (!existingFirstTouch) {
      safeSetLocal(FIRST_TOUCH_KEY, utmPayload);
    }
  }

  // Clean the URL if UTMs were embedded in the hash (keep anchor intact)
  if (anchor && Object.keys(hashParams).length > 0) {
    try {
      const cleanHash = `#${ANCHOR_ALIASES[anchor] || anchor}`;
      window.history.replaceState(null, '', window.location.pathname + window.location.search + cleanHash);
    } catch (_) { /* ignore */ }
  }

  // Scroll to intended anchor
  if (anchor) {
    const targetId = ANCHOR_ALIASES[anchor] || anchor;
    setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }
}

/**
 * Return attribution ready to merge into any form payload.
 * Priority order: current session > first-touch (localStorage, 30d).
 * Always includes a fresh landing_page + first_page_visited.
 */
export function getAttribution() {
  if (typeof window === 'undefined') return {};

  const session = safeGetSession(SESSION_KEY) || {};
  const firstTouch = safeGetLocal(FIRST_TOUCH_KEY) || {};
  const firstPage = safeGetLocal(FIRST_PAGE_KEY) || {};

  // For each UTM key: session wins if present, else first-touch fallback
  const pick = (k) => session[k] || firstTouch[k] || null;

  return {
    utm_source: pick('utm_source'),
    utm_campaign: pick('utm_campaign'),
    utm_medium: pick('utm_medium'),
    utm_content: pick('utm_content'),
    utm_term: pick('utm_term'),
    landing_page: session.landing_page || firstTouch.landing_page ||
      (window.location.pathname + window.location.search + window.location.hash) || null,
    first_page_visited: firstPage.first_page_visited ||
      (window.location.pathname + window.location.search + window.location.hash) || null,
  };
}
